import React, { ComponentProps } from 'react'
import { HStack } from '@/src/views/parts/bases/HStack'
import { VStack } from '@/src/views/parts/bases/VStack'
import { Center } from '@/src/views/parts/bases/Center'
import { Text } from '@/src/views/parts/bases/Text'
import Image, { StaticImageData } from 'next/image'
import { Space } from '@/src/views/parts/bases/Space'
import { useTranslation } from 'react-i18next'
import PlusIcon from '@/public/images/iconPlus.png'
import CraftResultTriangle from '@/public/images/craftResultTriangle.png'
import tofuNftLogo from '@/public/images/tofuNFTLogo.png'
import { PinkNeonButton } from '@/src/views/parts/buttons/PinkNeonButton'
import CraftAmSilhouette from '@/public/images/craftAmSilhouette.png'
import { Clickable } from '@/src/views/parts/bases/Clickable'
import { AMCraftDialog } from '@/src/views/dialogs/AMCraftDialog'
import CraftRomPlaceholder from '@/public/images/craftRomPlaceholder.png'
import CraftAccumulatorPlaceholder from '@/public/images/craftAccumulatorPlaceholder.png'
import CraftUpperCabinetPlaceholder from '@/public/images/craftUpperCabinetPlaceholder.png'
import CraftLowerCabinetPlaceholder from '@/public/images/craftLowerCabinetPlaceholder.png'
import { ExternalUrlService } from '@/src/services/ExternalUrlService'
import { Absolute } from '@/src/views/parts/bases/Absolute'
import ErrorIcon from '@/public/images/errorIcon.png'
import Teras from '@/public/images/teras.png'
import AKV from '@/public/images/akv.png'
import Vector from '@/public/images/vector.png'
import { t } from 'i18next'
import { StarArea } from '@/src/views/parts/nft/StarArea'
import { VideoPlayer } from '@/src/views/parts/commons/VideoPlayer'

type PartsType = 'AP' | 'Junk'

export type Props = {
  roms: ComponentProps<typeof PartsSelector>['items']
  accumulators: ComponentProps<typeof PartsSelector>['items']
  upperCabients: ComponentProps<typeof PartsSelector>['items']
  lowerCabinets: ComponentProps<typeof PartsSelector>['items']
  selectedRom?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
  selectedAccumulator?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
  selectedUpperCabinet?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
  selectedLowerCabinet?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
  creaftedAm?: { name: string; animationUrl: string; akvFee: number; terasFee: number }
  selectedAmPartsType: 'ROM' | 'Accumulator' | 'UpperCabinet' | 'LowerCabinet'
  craftState: 'Draft' | 'Depositable' | 'Depositing' | 'Craftable' | 'WaitingForCompletedDepositing'
  akvBalance: number
  terasBalance: number
  onSelectedROM: (id: string) => void
  onSelectedAccumulator: (id: string) => void
  onSelectedUpperCabinet: (id: string) => void
  onSelectedLowerCabinet: (id: string) => void
  onSelectPartsType: (partsType: Props['selectedAmPartsType']) => void
  onClickPrepare: () => void
  onClickCraft: () => void
  visibleCraftDialog: boolean
  onCompletedAMCraft: () => void
  onClickAMCraftDialogClose: () => void
  upperOverlayLabel: string | null
  lowerOverlayLabel: string | null
}

export const Layout = (props: Props) => {
  // 表示するAPを絞り込む
  const filterDisplayAP = (ap: ComponentProps<typeof PartsSelector>['items']) =>
    ap.filter((a) => a.type === 'Junk' || a.inAkiverseCounts + a.inWalletCounts > 0)

  return (
    <>
      <VStack className="h-full w-full p-8">
        <HStack className="relative h-96 w-full rounded-2xl  border border-[#805BE7] bg-black p-8">
          {props.craftState === 'WaitingForCompletedDepositing' ? (
            <Absolute className="top-4 left-4">
              <HStack className="items-center">
                <Image src={ErrorIcon} width={20} height={20} alt="" />
                <span className="ml-2 flex items-center font-bold text-[#DC325B]">
                  {t('ArcadeMachineManagerPage.craft.waitingForDeposit')}
                </span>
              </HStack>
            </Absolute>
          ) : null}
          <HStack className="flex-1">
            <Space />
            <VStack className="mt-14 items-center">
              <SelectedParts
                onClick={() => props.onSelectPartsType('ROM')}
                label={t('ArcadeMachineManagerPage.craft.romLabel')}
                active={props.selectedAmPartsType === 'ROM'}
                name={props.selectedRom?.name ?? ''}
                image={props.selectedRom?.image ?? CraftRomPlaceholder}
                disabled={props.craftState === 'WaitingForCompletedDepositing'}
                type={props.selectedRom?.type ?? 'AP'}
                junksPerPart={props.selectedRom?.junksPerPart}
                overlay={!!props.selectedRom ? '' : t('ArcadeMachineManagerPage.craft.firstDetermine') ?? ''}
              />
            </VStack>
            <Space />
            <Image src={PlusIcon} alt="" objectFit="contain" width={24} height={24} />
            <Space />
            <VStack className="mt-14 items-center">
              <SelectedParts
                onClick={() => props.onSelectPartsType('Accumulator')}
                label={t('ArcadeMachineManagerPage.craft.accumulatorLabel')}
                active={props.selectedAmPartsType === 'Accumulator'}
                name={props.selectedAccumulator?.name ?? ''}
                image={props.selectedAccumulator?.image ?? CraftAccumulatorPlaceholder}
                disabled={props.craftState === 'WaitingForCompletedDepositing' || !props.selectedRom}
                type={props.selectedAccumulator?.type ?? 'AP'}
                junksPerPart={props.selectedAccumulator?.junksPerPart}
              />
            </VStack>
            <Space />
            <Image src={PlusIcon} alt="" objectFit="contain" width={24} height={24} />
            <Space />
            <VStack className="mt-14 items-center">
              <SelectedParts
                onClick={() => props.onSelectPartsType('UpperCabinet')}
                label={t('ArcadeMachineManagerPage.craft.upperCabinetLabel')}
                active={props.selectedAmPartsType === 'UpperCabinet'}
                name={props.selectedUpperCabinet?.name ?? ''}
                image={props.selectedUpperCabinet?.image ?? CraftUpperCabinetPlaceholder}
                disabled={props.craftState === 'WaitingForCompletedDepositing' || !props.selectedRom}
                type={props.selectedUpperCabinet?.type ?? 'AP'}
                junksPerPart={props.selectedUpperCabinet?.junksPerPart}
                overlay={
                  !!props.upperOverlayLabel && !props.selectedUpperCabinet
                    ? t('ArcadeMachineManagerPage.craft.gradeRequired', { category: props.upperOverlayLabel })
                    : null
                }
              />
            </VStack>
            <Space />
            <Image src={PlusIcon} alt="" objectFit="contain" width={24} height={24} />
            <Space />
            <VStack className="mt-14 items-center">
              <SelectedParts
                onClick={() => props.onSelectPartsType('LowerCabinet')}
                label={t('ArcadeMachineManagerPage.craft.lowerCabinetLabel')}
                active={props.selectedAmPartsType === 'LowerCabinet'}
                name={props.selectedLowerCabinet?.name ?? ''}
                image={props.selectedLowerCabinet?.image ?? CraftLowerCabinetPlaceholder}
                disabled={props.craftState === 'WaitingForCompletedDepositing' || !props.selectedRom}
                type={props.selectedLowerCabinet?.type ?? 'AP'}
                junksPerPart={props.selectedLowerCabinet?.junksPerPart}
                overlay={
                  !!props.lowerOverlayLabel && !props.selectedLowerCabinet
                    ? t('ArcadeMachineManagerPage.craft.gradeRequired', { category: props.lowerOverlayLabel })
                    : null
                }
              />
            </VStack>
            <Space />
            <Center className="pl-4">
              <Image
                src={CraftResultTriangle}
                objectFit="contain"
                width={34}
                objectPosition="center"
                height={34}
                alt=""
              />
            </Center>
            <Space />
            <VStack className="items-center justify-center">
              <Text className="mb-2 text-xs text-neon-blue">
                {t('ArcadeMachineManagerPage.craft.craftedResultLabel')}
              </Text>
              <Center className="aspect-square w-44 overflow-hidden rounded-xl border border-[#805BE7]">
                {props.creaftedAm ? (
                  <VideoPlayer src={props.creaftedAm.animationUrl} loop />
                ) : (
                  <Center className="relative h-[80%] w-[80%]">
                    <Image src={CraftAmSilhouette} layout="fill" alt="" />
                  </Center>
                )}
              </Center>
              <Text className="my-3 text-base font-bold text-neon-blue">{props.creaftedAm?.name}</Text>
              {props.craftState === 'Draft' ? (
                <PinkNeonButton disabled>{t('ArcadeMachineManagerPage.craft.draftButton')}</PinkNeonButton>
              ) : null}
              {props.craftState === 'Depositable' ? (
                <PinkNeonButton onClick={props.onClickPrepare}>
                  {t('ArcadeMachineManagerPage.craft.prepareButton')}
                </PinkNeonButton>
              ) : null}
              {props.craftState === 'Depositing' ? <PinkNeonButton loading /> : null}
              {props.craftState === 'WaitingForCompletedDepositing' ? <PinkNeonButton loading /> : null}
              {props.craftState === 'Craftable' ? (
                <HStack className="items-center">
                  <CraftingFee
                    akvFee={props.creaftedAm?.akvFee || 0}
                    terasFee={props.creaftedAm?.terasFee || 0}
                    akvBalance={props.akvBalance || 0}
                    terasBalance={props.terasBalance || 0}
                  />
                  <PinkNeonButton
                    onClick={props.onClickCraft}
                    disabled={
                      (props.terasBalance || 0) < (props.creaftedAm?.terasFee || 0) &&
                      (props.akvBalance || 0) < (props.creaftedAm?.akvFee || 0)
                    }
                  >
                    {t('ArcadeMachineManagerPage.craft.craftButton')}
                  </PinkNeonButton>
                </HStack>
              ) : null}
            </VStack>
            <Space />
          </HStack>
        </HStack>
        {props.craftState !== 'WaitingForCompletedDepositing' ? (
          <VStack className="mt-4">
            {props.selectedAmPartsType == 'ROM' ? (
              <PartsSelector
                selectedPartId={props.selectedRom?.id}
                label={t('ArcadeMachineManagerPage.craft.romLabel')}
                items={filterDisplayAP(props.roms)}
                onSelected={props.onSelectedROM}
              />
            ) : null}
            {props.selectedAmPartsType == 'Accumulator' ? (
              <PartsSelector
                selectedPartId={props.selectedAccumulator?.id}
                label={t('ArcadeMachineManagerPage.craft.accumulatorLabel')}
                items={filterDisplayAP(props.accumulators)}
                onSelected={props.onSelectedAccumulator}
              />
            ) : null}
            {props.selectedAmPartsType == 'UpperCabinet' ? (
              <PartsSelector
                selectedPartId={props.selectedUpperCabinet?.id}
                label={t('ArcadeMachineManagerPage.craft.upperCabinetLabel')}
                items={filterDisplayAP(props.upperCabients)}
                onSelected={props.onSelectedUpperCabinet}
              />
            ) : null}
            {props.selectedAmPartsType == 'LowerCabinet' ? (
              <PartsSelector
                selectedPartId={props.selectedLowerCabinet?.id}
                label={t('ArcadeMachineManagerPage.craft.lowerCabinetLabel')}
                items={filterDisplayAP(props.lowerCabinets)}
                onSelected={props.onSelectedLowerCabinet}
              />
            ) : null}
          </VStack>
        ) : null}
      </VStack>
      {props.visibleCraftDialog ? (
        <AMCraftDialog
          parts={{
            rom: props.selectedRom,
            accumulator: props.selectedAccumulator,
            upperCabinet: props.selectedUpperCabinet,
            lowerCabinet: props.selectedLowerCabinet,
          }}
          akvFee={props.creaftedAm?.akvFee || 0}
          terasFee={props.creaftedAm?.terasFee || 0}
          onCompleted={props.onCompletedAMCraft}
          onClickClose={props.onClickAMCraftDialogClose}
        />
      ) : null}
    </>
  )
}

const NoPartsLink = () => {
  const { t } = useTranslation()
  return (
    <VStack className="items-center justify-center">
      <Text className="mb-1 text-center text-sm text-white">
        {t('ArcadeMachineManagerPage.craft.purchaseFromTofuNFT')}
      </Text>
      <a
        className="flex"
        href={ExternalUrlService.getTofuNftAkiverseAPPage()}
        target="_blank"
        rel="noopener noreferrer"
      >
        <HStack className="mt-2 items-center justify-center rounded-full border border-akiverse-pink px-5 py-2">
          <Center className="relative h-6 w-6">
            <Image src={tofuNftLogo} layout="fill" alt="" />
          </Center>
          <Text className="ml-4 text-white">LINK</Text>
        </HStack>
      </a>
    </VStack>
  )
}

const PartsSelector = (props: {
  label: string
  selectedPartId?: string
  items: Array<
    | {
        type: 'AP'
        id: string
        subCategory: string
        image: string
        name: string
        inWalletCounts: number
        inAkiverseCounts: number
        selectablePartId: string
        rarity: number
        disabled?: boolean
      }
    | {
        type: 'Junk'
        id: string
        subCategory: string
        image: string
        name: string
        inAkiverseCounts: number
        selectablePartId: string
        rarity: number
        junksPerPart: number
        disabled?: boolean
      }
  >
  onSelected: (id: string) => void
}) => {
  return (
    <VStack className="w-full">
      <HStack className="items-baseline">
        <Text className="text-lg font-bold text-white">{props.label}</Text>
        <Text className="ml-2 text-sm text-white text-opacity-50">({props.items.length})</Text>
      </HStack>
      {props.items.length > 0 ? (
        <HStack className="mt-2 w-full overflow-x-auto pb-4">
          <HStack>
            {props.items.map((item) => (
              <Center key={item.id} className="relative mx-2">
                {item.disabled ? (
                  <Absolute className="top-0 left-0 z-10 h-full w-full rounded-[10px] bg-black bg-opacity-50" />
                ) : null}
                <Clickable
                  className={`
                    h-[230px] w-40 overflow-hidden rounded-[10px]  border-2 bg-black bg-gradient-to-b from-[rgba(0,0,0,0)] via-[rgba(0,0,0,0)] to-[#805BE760]
                    ${
                      props.selectedPartId === item.selectablePartId
                        ? 'border-akiverse-pink'
                        : item.type === 'AP'
                        ? 'border-[#805BE7]'
                        : 'border-[#5F5B7A]'
                    }`}
                  onClick={() => props.onSelected(item.selectablePartId)}
                  disabled={item.disabled}
                >
                  <VStack className="h-full w-full bg-[url('/images/gameCenterCellTextBg.png')] bg-repeat">
                    <div
                      className="relative aspect-square w-full bg-black bg-cover bg-center"
                      style={{ backgroundImage: `url('${item.image}')` }}
                    >
                      <Absolute className="left-1.5 bottom-1.5 w-full">
                        <StarArea parentKey={item.id} rarity={item.rarity} />
                      </Absolute>
                    </div>
                    <VStack className="w-full flex-1 justify-end px-2 pb-1">
                      <span
                        className={`my-1 max-w-full flex-1 overflow-hidden text-ellipsis text-left text-sm font-bold text-white ${
                          item.type === 'AP' ? 'whitespace-nowrap' : ''
                        }`}
                      >
                        {item.name}
                      </span>
                      {item.type === 'AP' ? (
                        <HStack className="w-full items-baseline justify-end">
                          <Text className="text-sm font-bold text-white">In wallet</Text>
                          <Text className="mx-2 text-xs font-bold text-white">x</Text>
                          <Text className="text-sm font-bold text-neon-blue">
                            {item.inWalletCounts || '-'}
                          </Text>
                        </HStack>
                      ) : null}
                      <HStack className="w-full items-baseline justify-end">
                        <Text className="text-sm font-bold text-white">In Akiverse</Text>
                        <Text className="mx-2 text-xs font-bold text-white">x</Text>
                        <Text className="text-sm font-bold text-neon-blue">
                          {item.inAkiverseCounts || '-'}
                        </Text>
                      </HStack>
                    </VStack>
                  </VStack>
                </Clickable>
              </Center>
            ))}
          </HStack>
        </HStack>
      ) : (
        <VStack className="h-40 w-full items-center justify-center">
          <NoPartsLink />
        </VStack>
      )}
    </VStack>
  )
}

const SelectedParts = (props: {
  disabled: boolean
  onClick: () => void
  label: string
  name: string
  active: boolean
  image: string | StaticImageData
  type: PartsType
  junksPerPart?: number
  overlay?: string | null
}) => {
  return (
    <Clickable disabled={props.disabled} onClick={props.onClick}>
      <VStack className="items-center">
        <VStack className="flex-1 justify-end">
          <Text className="mb-2 text-xs font-bold text-neon-blue">{props.label}</Text>
        </VStack>
        <Center
          className={`
          relative aspect-square w-40 overflow-hidden rounded-xl border-2
          ${props.disabled ? 'opacity-50' : ''}
          ${props.active ? 'border-akiverse-pink' : 'border-[#805BE7]'}`}
        >
          <Image src={props.image} layout="fill" alt="" />
          {!!props.overlay && (
            <Absolute className="h-full w-full items-center justify-center">
              <Text className="text-base font-bold text-akiverse-yellow">{props.overlay}</Text>
            </Absolute>
          )}
        </Center>
        <VStack className="flex-1">
          <Text className="mt-2 w-40 text-xs font-bold text-white">{props.name}</Text>
          {props.type === 'Junk' ? (
            <Center className="mt-1 w-full flex-col">
              <Text className="text-xs font-bold text-akiverse-pink">
                Junk {props.name} x {props.junksPerPart}
              </Text>
            </Center>
          ) : null}
        </VStack>
      </VStack>
    </Clickable>
  )
}

const CraftingFee = (props: {
  akvFee: number
  terasFee: number
  akvBalance: number
  terasBalance: number
}) => {
  const { akvFee, terasFee, akvBalance, terasBalance } = props
  const isError = terasBalance < terasFee && akvBalance < akvFee
  return (
    <VStack className="items-left w-full justify-center">
      <VStack>
        <Text className="text-base text-white">Crafting fee</Text>
        <HStack className="items-center justify-start">
          <div className="h-6 w-6">
            <Image src={AKV} alt="AKV" objectFit="contain" />
          </div>
          <Text className={`ml-2 text-2xl ${isError ? 'text-[#DC325B]' : 'text-primary-textActive'}`}>
            {akvFee.toLocaleString()}
          </Text>
          <div className="ml-6 h-6 w-6">
            <Image src={Teras} alt="Teras" objectFit="contain" />
          </div>
          <Text className={`ml-2 text-2xl ${isError ? 'text-[#DC325B]' : 'text-primary-textActive'}`}>
            {terasFee.toLocaleString()}
          </Text>
        </HStack>
      </VStack>
      {isError && (
        <Absolute className="top-14 w-full">
          <HStack className="w-full items-center gap-x-1 self-start">
            <Image src={Vector} alt="Teras" objectFit="contain" height={14} width={14} />
            <Text className="whitespace-nowrap text-sm text-[#DC325B]">
              {t('ArcadeMachineManagerPage.craft.insufficientError')}
            </Text>
          </HStack>
        </Absolute>
      )}
    </VStack>
  )
}
